// =============================================================
//  ManageCentersScreen — Gestion des centres (écoles)
// =============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import {
  Card,
  Button,
  Badge,
  EmptyState,
  Loading,
  Avatar,
  DataRow,
  SearchBar,
  StatCard,
} from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

export default function ManageCentersScreen({ navigation }) {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [centers, setCenters] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const ref = collection(db, 'centers');
      const snap = await getDocs(ref);
      setCenters(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.log('centers load error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return centers;
    return centers.filter((c) =>
      (c.centerName || c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [centers, search]);

  return (
    <AdminLayout title="Gestion des centres" subtitle="Établissements partenaires de MyConcours">
      <View style={styles.statsRow}>
        <StatCard
          label="Total centres"
          value={centers.length}
          icon="school"
          accent={colors.primary}
          loading={loading}
        />
        <StatCard
          label="Actifs"
          value={centers.filter((c) => c.status === 'active' || !c.status).length}
          icon="check-circle-outline"
          accent={colors.success}
          loading={loading}
        />
      </View>

      <View style={styles.toolbar}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <SearchBar
            value={search}
            onChangeText={setSearch}
            placeholder="Rechercher un centre..."
          />
        </View>
      </View>

      <Card noPadding>
        {loading ? (
          <Loading label="Chargement..." />
        ) : filtered.length === 0 ? (
          <EmptyState icon="school-outline" title="Aucun centre" />
        ) : (
          filtered.map((c, i) => {
            const name = c.centerName || c.name || 'Sans nom';
            const isBlocked = c.status === 'blocked';
            return (
              <DataRow
                key={c.id}
                last={i === filtered.length - 1}
                onPress={() => navigation.navigate('CenterDetailScreen', { center: c })}
              >
                <Avatar src={c.photoURL || c.imageUrl} name={name} size={40} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.rowTitle} numberOfLines={1}>{name}</Text>
                  <Text style={styles.rowSubtitle} numberOfLines={1}>
                    {c.email || c.phoneNumber || '—'}
                  </Text>
                </View>
                <Badge variant={isBlocked ? 'danger' : 'success'}>
                  {isBlocked ? 'Bloqué' : 'Actif'}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="arrow-right"
                  onPress={() => navigation.navigate('CenterDetailScreen', { center: c })}
                />
              </DataRow>
            );
          })
        )}
      </Card>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  statsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg, marginBottom: spacing.lg },
  toolbar: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginBottom: spacing.lg },
  rowTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  rowSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
});
